import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import * as haptic from '@theme/haptic';

/** @typedef {import('./types.js').ProductVariant} ProductVariant */

/**
 * @param {string[]} a
 * @param {string[]} b
 */
function arraysEqual(a, b) {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

export default class VariantPicker extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.variantChanged);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.variantChanged);
  }

  /**
   * @param {Event} event
   */
  variantChanged = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    haptic.light();

    if (event.target instanceof HTMLSelectElement) {
      for (const option of event.target.options) {
        option.removeAttribute('selected');
      }
      event.target.options[event.target.selectedIndex]?.setAttribute('selected', 'selected');
    }

    const variant = this.selectedVariant;

    this.dispatchEvent(new VariantSelectedEvent({
      id: variant?.id ? String(variant.id) : '',
    }));

    if (this.dataset.templateProductMatch === 'true') {
      const url = new URL(window.location.href);
      if (variant?.id) {
        url.searchParams.set('variant', String(variant.id));
      } else {
        url.searchParams.delete('variant');
      }
      history.replaceState({}, '', url.toString());
    }

    this.dispatchEvent(
      new VariantUpdateEvent(variant || null, this.id || '', {
        html: document,
        productId: this.dataset.productId || '',
      })
    );
  };

  get selectedValues() {
    const indexes = new Set(
      Array.from(this.querySelectorAll('[data-option-index]'))
        .map((element) => (element instanceof HTMLElement ? Number(element.dataset.optionIndex) : NaN))
        .filter((index) => Number.isFinite(index))
    );

    return Array.from(indexes)
      .sort((a, b) => a - b)
      .map((index) => {
        const checked = this.querySelector(`input[type="radio"][data-option-index="${index}"]:checked`);
        if (checked instanceof HTMLInputElement) return checked.value;

        const select = this.querySelector(`select[data-option-index="${index}"]`);
        if (select instanceof HTMLSelectElement) return select.value;

        return '';
      });
  }

  /** @returns {ProductVariant[]} */
  get #variants() {
    const json = this.querySelector('script[type="application/json"]')?.textContent;
    if (!json) return [];
    try {
      return /** @type {ProductVariant[]} */ (JSON.parse(json));
    } catch {
      return [];
    }
  }

  get selectedVariant() {
    const values = this.selectedValues;
    const match = this.#variants.find((variant) => arraysEqual(variant.options || [], values));
    return match || null;
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
